/**
 * The answers for a study that does not exist yet.
 *
 * As in the prototype, a new study is only created when someone asks the community: until then the
 * audience, message and promises are a draft. It is kept in local storage, so a refresh or a closed
 * tab does not lose a half-written message.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export type Draft = {
  audienceId: string | null;
  audienceName: string | null;
  name: string;
  text: string;
  facts: string[];
  factsSaved: boolean; // promises are optional, but the step counts as done once they have been looked at
};

const EMPTY: Draft = { audienceId: null, audienceName: null, name: "", text: "", facts: [], factsSaved: false };
const KEY = "new-study-draft";

type DraftApi = { draft: Draft; update: (changes: Partial<Draft>) => void; clear: () => void };

const DraftContext = createContext<DraftApi | null>(null);

export function DraftProvider({ children }: { children: ReactNode }) {
  const [draft, setDraft] = useState<Draft>(load);

  const update = useCallback((changes: Partial<Draft>) => {
    setDraft((current) => {
      const next = { ...current, ...changes };
      save(next);
      return next;
    });
  }, []);
  const clear = useCallback(() => {
    save(EMPTY);
    setDraft(EMPTY);
  }, []);

  const value = useMemo(() => ({ draft, update, clear }), [draft, update, clear]);
  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
}

export function useDraft(): DraftApi {
  const value = useContext(DraftContext);
  if (!value) throw new Error("useDraft must be used inside <DraftProvider>");
  return value;
}

function load(): Draft {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? { ...EMPTY, ...(JSON.parse(raw) as Partial<Draft>) } : EMPTY;
  } catch {
    return EMPTY;
  }
}

function save(draft: Draft): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(draft));
  } catch {
    /* the draft still works for this visit */
  }
}
